import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiPlay, FiChevronLeft, FiChevronRight, FiVolume2, FiVolumeX } from 'react-icons/fi';
import { FaInstagram, FaTiktok, FaFacebook } from 'react-icons/fa';
import './HomePage.css';

// Custom hook for scroll-triggered animations - DISABLED ON MOBILE
const useScrollAnimation = () => {
  const observerRef = useRef(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  const observe = useCallback((element) => {
    if (!element) return;

    // On mobile - just show content immediately, no observer
    if (isMobile) {
      element.classList.add('animate-in');
      const children = element.querySelectorAll('.animate-child');
      children.forEach((child) => {
        child.classList.add('animate-in');
      });
      return;
    }

    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('animate-in');
              const children = entry.target.querySelectorAll('.animate-child');
              children.forEach((child, index) => {
                child.style.transitionDelay = `${index * 0.1}s`;
                child.classList.add('animate-in');
              });
            }
          });
        },
        { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
      );
    }

    observerRef.current.observe(element);
  }, [isMobile]);

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return observe;
};

const HomePage = () => {
  const [playingVideo, setPlayingVideo] = useState(null);
  const [heroMuted, setHeroMuted] = useState(true);
  const heroVideoRef = useRef(null);
  const floatingCtaRef = useRef(null);
  const videosGridRef = useRef(null);
  const observe = useScrollAnimation();

  // Scroll-driven floating CTA with mobile scroll lock
  useEffect(() => {
    const SCROLL_DISTANCE = 400;
    let virtualScroll = 0;
    let hasScrolledPastHero = false;
    let lastTouchY = 0;
    let lastTouchX = 0;
    let touchStartedInCarousel = false;

    const updateCta = (progress) => {
      const el = floatingCtaRef.current;
      if (!el) return;
      const clamped = Math.max(0, Math.min(1, progress));
      el.style.opacity = clamped;
      const offsetY = 40 * (1 - clamped);
      el.style.transform = `translate(-50%, calc(-50% + ${offsetY}vh))`;
      el.style.pointerEvents = clamped > 0.5 ? 'auto' : 'none';
    };

    const handleWheel = (e) => {
      // Once user has scrolled past hero, don't hijack scroll anymore
      if (hasScrolledPastHero) return;
      if (window.scrollY > 5) return;

      if (virtualScroll < SCROLL_DISTANCE && e.deltaY > 0) {
        // Scrolling down at top, buttons not fully shown → capture
        e.preventDefault();
        virtualScroll = Math.min(virtualScroll + e.deltaY, SCROLL_DISTANCE);
        updateCta(virtualScroll / SCROLL_DISTANCE);
      } else if (virtualScroll > 0 && e.deltaY < 0) {
        // Scrolling up at top → hide buttons
        e.preventDefault();
        virtualScroll = Math.max(virtualScroll + e.deltaY, 0);
        updateCta(virtualScroll / SCROLL_DISTANCE);
      }
    };

    const handleTouchStart = (e) => {
      lastTouchY = e.touches[0].clientY;
      lastTouchX = e.touches[0].clientX;
      touchStartedInCarousel = e.target.closest('.videos-grid') !== null;
    };

    const handleTouchMove = (e) => {
      // Never interfere with carousel touches
      if (touchStartedInCarousel) return;
      // Once user has scrolled past hero, don't hijack scroll anymore
      if (hasScrolledPastHero) return;
      if (window.scrollY > 5) return;

      const touchY = e.touches[0].clientY;
      const touchX = e.touches[0].clientX;
      const deltaY = lastTouchY - touchY;
      const deltaX = Math.abs(touchX - lastTouchX);
      lastTouchY = touchY;
      lastTouchX = touchX;

      if (deltaX > Math.abs(deltaY) || !e.cancelable) return;

      if (virtualScroll < SCROLL_DISTANCE && deltaY > 0) {
        e.preventDefault();
        virtualScroll = Math.min(virtualScroll + deltaY * 2, SCROLL_DISTANCE);
        updateCta(virtualScroll / SCROLL_DISTANCE);
      } else if (virtualScroll > 0 && deltaY < 0) {
        e.preventDefault();
        virtualScroll = Math.max(virtualScroll + deltaY * 2, 0);
        updateCta(virtualScroll / SCROLL_DISTANCE);
      }
    };

    const handleScroll = () => {
      // Mark that user has scrolled past the hero section
      if (window.scrollY > window.innerHeight) {
        hasScrolledPastHero = true;
      }
      // When scrolling back to top after exploring, restore buttons instantly
      if (hasScrolledPastHero && window.scrollY <= 5) {
        virtualScroll = SCROLL_DISTANCE;
        updateCta(1);
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('scroll', handleScroll, { passive: true });
    updateCta(0);

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleHeroClick = () => {
    if (heroVideoRef.current) {
      heroVideoRef.current.muted = !heroVideoRef.current.muted;
      setHeroMuted(heroVideoRef.current.muted);
    }
  };

  const scrollVideos = (direction) => {
    const grid = videosGridRef.current;
    if (!grid) return;
    const scrollAmount = 220;
    // RTL: scrollLeft is 0 at start (right edge) and goes negative
    const atStart = grid.scrollLeft >= -10;
    const atEnd = grid.scrollLeft <= -(grid.scrollWidth - grid.clientWidth - 10);

    if (direction === 'left' && atEnd) {
      // Wrap to start
      grid.scrollTo({ left: 0, behavior: 'smooth' });
    } else if (direction === 'right' && atStart) {
      // Wrap to end
      grid.scrollTo({ left: -(grid.scrollWidth - grid.clientWidth), behavior: 'smooth' });
    } else {
      grid.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleVideoClick = (videoId, e) => {
    const video = e.currentTarget.querySelector('video');
    if (playingVideo === videoId) {
      video.pause();
      setPlayingVideo(null);
    } else {
      video.play();
      setPlayingVideo(videoId);
    }
  };

  const brands = [
    { name: 'Clalit', logo: '/brands/clalit.png' },
    { name: 'Lacoste', logo: '/brands/lacoste.png' },
    { name: 'Mega Sport', logo: '/brands/mega_sport.png' },
    { name: 'Decathlon', logo: '/brands/decathlon.png' },
    { name: 'Amsiragas', logo: '/brands/amsiragas.png' },
    { name: 'Tempo', logo: '/brands/tempo.png' },
    { name: 'Harley Davidson', logo: '/brands/harley_davidson.png' },
    { name: 'Discount Bank', logo: '/brands/discountbank.png' },
    { name: 'Yes', logo: '/brands/yes.png' },
    { name: 'Timberland', logo: '/brands/timberland.png' },
    { name: 'Toys R Us', logo: '/brands/toysrus.png' },
    { name: 'Benda', logo: '/brands/benda.png' },
    { name: 'Elite', logo: '/brands/elite.png' },
    { name: 'Similac', logo: '/brands/similac.png' }
  ];

  const videos = [
    { id: 1, url: 'https://reshetimes.co.il/videoToAiweon/vid-1.mp4' },
    { id: 2, url: 'https://reshetimes.co.il/videoToAiweon/vid-2.mp4' },
    { id: 4, url: 'https://reshetimes.co.il/videoToAiweon/vid-4.mp4' },
    { id: 5, url: 'https://reshetimes.co.il/videoToAiweon/vid-5.mp4' },
    { id: 6, url: 'https://reshetimes.co.il/videoToAiweon/vid-6.mp4' },
    { id: 7, url: `${process.env.PUBLIC_URL}/videos/vid-7.mp4` },
    { id: 8, url: `${process.env.PUBLIC_URL}/videos/vid-8.mp4` },
    { id: 9, url: `${process.env.PUBLIC_URL}/videos/vid-9.mp4` },
    { id: 10, url: `${process.env.PUBLIC_URL}/videos/vid-10.mp4` },
    { id: 11, url: `${process.env.PUBLIC_URL}/videos/vid-11.mp4` },
    { id: 12, url: `${process.env.PUBLIC_URL}/videos/vid-12.mp4` },
    { id: 13, url: `${process.env.PUBLIC_URL}/videos/vid-13.mp4` },
    { id: 14, url: `${process.env.PUBLIC_URL}/videos/vid-14.mp4` },
    { id: 15, url: `${process.env.PUBLIC_URL}/videos/vid-15.mp4` },
    { id: 16, url: `${process.env.PUBLIC_URL}/videos/vid-16.mp4` },
    { id: 17, url: `${process.env.PUBLIC_URL}/videos/vid-17.mp4` },
    { id: 18, url: `${process.env.PUBLIC_URL}/videos/vid-18.mp4` }
  ];

  const whyUs = [
    {
      title: 'יוצרים מאומתים',
      description: 'כל יוצרי התוכן שלנו עוברים תהליך אימות קפדני לפני שהם נכנסים למערכת ולעבודה משותפת איתנו על מנת לתת לכם את הביטחון המוחלט לייצוג המותג שלכם והערכים שלו.'
    },
    {
      title: 'Weon Pixel',
      description: 'הטמיעו מעקב מהיר בכל הפלטפורמות שלכם. קבלו נתונים מדויקים על ביצועי הקמפיינים בזמן אמת, וקבלו תובנות לשיפור אופטימלי של התוצאות העסקיות.'
    },
    {
      title: 'תמיכה מקצועית',
      description: 'צוות התמיכה שלנו זמין עבורכם לאורך כל הדרך, מהאפיון הראשון ועד לסיום הקמפיין.'
    },
    {
      title: 'תוכן איכותי',
      description: 'יוצרים מקצועיים שיודעים ליצור תוכן שמושך תשומת לב ומניע לפעולה. כל תוכן עובר בקרת איכות לפני שליחה.'
    }
  ];

  const steps = [
    { number: '1', title: 'בחירת המשפיען/נית שלכם', description: 'תבחרו את היוצרים המתאימים עבורכם מתוך קהילה איכותית שתתחבר לערכי העסק והמותג שלכם באופן מדויק' },
    { number: '2', title: 'דיוק מסרים', description: 'אנו נדייק את המסרים שחשוב ביותר לכם להעביר, ולקבל את התוכן המדויק והממיר ביותר עבורכם.' },
    { number: '3', title: 'מוכנים?', description: 'העלאת התוכן המאושר וחשיפה גדולה וממוקדת בכל הפלטפורמות הרלוונטיות למותג שלכם.' }
  ];

  return (
    <div className="home-page">
      {/* ===== HERO VIDEO - Full Width with scroll-hijacked CTA ===== */}
      <section className="hero-video-section" onClick={handleHeroClick}>
        <video
          ref={heroVideoRef}
          className="hero-video-player"
          autoPlay
          muted
          loop
          playsInline
          poster={`${process.env.PUBLIC_URL}/hero-video-poster.jpg`}
          src={`${process.env.PUBLIC_URL}/hero-video.mp4`}
        />
        <div className={`hero-video-mute-indicator ${heroMuted ? '' : 'is-unmuted'}`}>
          {heroMuted ? <FiVolumeX /> : <FiVolume2 />}
        </div>
        <div ref={floatingCtaRef} className="hero-floating-cta" onClick={(e) => e.stopPropagation()}>
          <Link to="/register?type=creator" className="btn-floating-creator">
            משפיען? הצטרף
          </Link>
          <Link to="/register?type=brand" className="btn-floating-brand">
            מותג? התחל קמפיין
          </Link>
        </div>
      </section>


      {/* ===== 2. VIDEOS SECTION - תוכן היוצרים ===== */}
      <section className="videos-section scroll-animate" ref={observe}>
        <div className="container">
          <h2 className="section-title animate-child">תוכן מהיוצרים שלנו</h2>
          <div className="videos-wrapper animate-child">
            <button className="videos-nav videos-nav-left" onClick={() => scrollVideos('left')}>
              <FiChevronLeft />
            </button>
            <div className="videos-grid" ref={videosGridRef}>
              {videos.map((video) => (
                <div
                  key={video.id}
                  className="video-card"
                  onClick={(e) => handleVideoClick(video.id, e)}
                >
                  <video src={video.url} loop playsInline />
                  {playingVideo !== video.id && (
                    <div className="video-overlay">
                      <FiPlay />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <button className="videos-nav videos-nav-right" onClick={() => scrollVideos('right')}>
              <FiChevronRight />
            </button>
          </div>
        </div>
      </section>

      {/* ===== 3. BRANDS MARQUEE - Logo Strip ===== */}
      <section className="brand-marquee-section">
        <div className="brands-track">
          {[...brands, ...brands].map((brand, index) => (
            <img
              key={index}
              src={brand.logo}
              alt={brand.name}
              className="brand-logo-item"
            />
          ))}
        </div>
      </section>

      {/* ===== 4. HOW IT WORKS - רקע שחור ===== */}
      <section className="how-it-works-section scroll-animate" ref={observe}>
        <div className="container">
          <h2 className="section-title animate-child">איך זה עובד?</h2>
          <div className="steps-grid">
            {steps.map((step, index) => (
              <div key={index} className="step-card animate-child">
                <div className="step-number">{step.number}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 5. WHY US SECTION ===== */}
      <section className="why-us-section scroll-animate" ref={observe}>
        <div className="container">
          <h2 className="section-title-dark animate-child">למה לעבוד איתנו?</h2>
          <div className="why-us-grid">
            {whyUs.map((item, index) => (
              <div key={index} className="why-us-card animate-child">
                <span className="why-us-number">{String(index + 1).padStart(2, '0')}</span>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 6. WHITE CTA SECTION - מוכנים להתחיל ===== */}
      <section className="cta-white-section scroll-animate" ref={observe}>
        <div className="container">
          <div className="cta-white-content">
            <h2 className="animate-child">מוכנים להתחיל?</h2>
            <p className="animate-child">הצטרפו לאלפי מותגים ויוצרים שכבר עובדים יחד</p>
            <div className="cta-white-actions animate-child">
              <Link to="/register?type=creator" className="btn-cta-secondary">
                הצטרפו כיוצרים
              </Link>
              <Link to="/register?type=brand" className="btn-cta-primary">
                התחילו קמפיין
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 7. PLATFORMS SECTION ===== */}
      <section className="platforms-section scroll-animate" ref={observe}>
        <div className="container">
          <h2 className="section-title animate-child">פלטפורמות נתמכות</h2>
          <div className="platforms-grid">
            <div className="platform-card animate-child">
              <FaTiktok style={{ color: 'var(--text-primary)' }} />
              <span>TikTok</span>
            </div>
            <a href="https://www.instagram.com/aiweon_" target="_blank" rel="noopener noreferrer" className="platform-card animate-child">
              <FaInstagram style={{ color: '#E1306C' }} />
              <span>Instagram</span>
            </a>
            <a href="https://www.facebook.com/profile.php?id=61587479324161" target="_blank" rel="noopener noreferrer" className="platform-card animate-child">
              <FaFacebook style={{ color: '#1877F2' }} />
              <span>Facebook</span>
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default HomePage;
